import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme, ThemeButton } from './theme';
import { Icon } from './icons';
import { usePolling } from './poll';

// TM RUSH team panel (Carol, Belle, Maurício): one row per process, grouped by stage, with the action of each row.
// Access: open /ops#k=<key> once; the key stays in this browser.
const API = 'https://n8n-hag0.srv1897462.hstgr.cloud/webhook';
const KEY_STORE = 'tmr_ops_key';
const NAME_STORE = 'tmr_ops_name';

const readKey = () => {
  try {
    const m = window.location.hash.match(/k=([A-Za-z0-9_-]+)/);
    if (m) {
      try { localStorage.setItem(KEY_STORE, m[1]); } catch (e) { /* storage blocked */ }
      window.history.replaceState(null, '', window.location.pathname);
      return m[1];
    }
    return localStorage.getItem(KEY_STORE) || '';
  } catch (e) {
    return '';
  }
};
const readName = () => { try { return localStorage.getItem(NAME_STORE) || ''; } catch (e) { return ''; } };

const fmt = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

const NEXT = {
  paid: 'Aguardando a ativação automática do pedido.',
  poa_sent: 'Aguardando os dados do pedido e a procuração assinada. O cliente envia tudo pela área dele. Se a procuração chegar por e-mail, clique em "Procuração recebida".',
  poa_received: 'Procuração recebida. Com os dados completos, a ordem de serviço de registro aparece no topo do painel.',
  filed: 'Protocolado. A vigilância semanal cuida do resto.',
  watch: 'Protocolado e com vigilância ativa. Nada a fazer até o próximo despacho.',
};

const OWNER_PT = { company: 'Empresa', individual: 'Pessoa física' };
const TL_ICON = { paid: 'card', poa_sent: 'mail', poa_upload: 'docSign', poa_received: 'docSign', details: 'form', upload: 'clip', filed: 'landmark', publication: 'newspaper', watch: 'eye', os: 'folder', note: 'message', message: 'message', grant: 'award', rpi: 'newspaper' };
const fmtFull = (s) => { const d = new Date(s); if (Number.isNaN(d.getTime())) return ''; return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
const Badge = ({ ok, children }) => <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${ok ? 'bg-primary/15 text-primary-dark' : 'bg-amber-50 text-amber-800'}`}>{children}</span>;
const DetailsBlock = ({ o }) => {
  const d = o.details || {};
  const ck = o.checklist || {};
  const addr = [d.addr_street, d.addr_city, [d.addr_region, d.addr_postal].filter(Boolean).join(' '), d.addr_country].filter(Boolean).join(', ');
  const goods = d.goods || {};
  const rows = [
    ['Titular', d.owner_name], ['Tipo', OWNER_PT[d.owner_type]], [d.owner_type === 'individual' ? 'Nacionalidade' : 'Constituída em', [d.owner_state, d.owner_country].filter(Boolean).join(', ')],
    ['Registro / ID', d.owner_reg_no || d.owner_id_no], ['Endereço', addr], ['Signatário', [d.signatory_name, d.signatory_title].filter(Boolean).join(', ')],
    ['Apresentação', o.mark_type_pt], ['Elemento nominativo', d.mark_type === 'figurative' ? '' : d.mark_text], ['Tradução', d.mark_translation],
    ...(d.goods_help ? [['Especificação', 'Cliente pediu sugestão da TM RUSH'], ['Atividade', d.business_desc]] : Object.keys(goods).map((k) => [k === 'all' ? 'Produtos/serviços' : `Classe ${k}`, goods[k]])),
    ['Prioridade', d.priority ? [d.priority_country, d.priority_number, d.priority_date].filter(Boolean).join(' · ') : ''],
    ['Contato', [d.contact_name, d.contact_email].filter(Boolean).join(' · ')], ['Ref. cliente', d.client_ref], ['Observações', d.notes],
  ].filter((r) => r[1]);
  const st = d.stripe || null;
  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-text/50 mr-1">Dados do cliente</span>
        <Badge ok={ck.owner}>Titular</Badge><Badge ok={ck.mark}>Marca{ck.needs_logo ? (ck.has_logo ? ' + logo' : ' (falta logo)') : ''}</Badge><Badge ok={ck.goods}>Produtos/serviços</Badge><Badge ok={ck.poa}>Procuração</Badge>
      </div>
      {rows.length ? (
        <dl className="text-sm space-y-1.5 bg-gray-50 rounded-xl p-3">
          {rows.map(([k, v]) => (
            <div key={k} className="flex gap-3"><dt className="text-text/50 w-28 shrink-0">{k}</dt><dd className="text-text break-words min-w-0 whitespace-pre-line">{v}</dd></div>
          ))}
        </dl>
      ) : <div className="text-sm text-text/50">O cliente ainda não preencheu os dados.</div>}
      {st && (
        <div className="mt-3 bg-gray-50 rounded-xl p-3 text-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-1.5">Pagamento (Stripe)</div>
          <div className="text-text"><b>{st.amount}</b> em {fmt(st.date)} · {st.card}</div>
          {(st.billing_name || st.billing_email) && <div className="text-text/70">{[st.billing_name, st.billing_email].filter(Boolean).join(' · ')}</div>}
          {st.billing_address && <div className="text-text/70">{st.billing_address}</div>}
          {st.receipt_url && <a href={st.receipt_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-primary-dark font-semibold hover:underline">Ver o recibo do Stripe</a>}
        </div>
      )}
    </div>
  );
};

// Prazos e marcos de cada processo (vigilância), despachos do INPI e colidências que a vigilância achou
const dayTone = (n) => (n < 0 ? 'bg-gray-100 text-gray-500' : n <= 7 ? 'bg-red-50 text-red-700' : n <= 30 ? 'bg-amber-50 text-amber-800' : 'bg-primary/10 text-primary-dark');
const dayText = (n) => (n < 0 ? `encerrado há ${-n} ${-n === 1 ? 'dia' : 'dias'}` : n === 0 ? 'hoje' : `faltam ${n} ${n === 1 ? 'dia' : 'dias'}`);
const dBr = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '');
const WatchBlock = ({ w }) => {
  if (!w || !w.active) return null;
  return (
    <div className="mb-4 space-y-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-2">Prazos e marcos {w.last_rpi ? <span className="normal-case font-normal">· última RPI lida: {w.last_rpi}</span> : null}</div>
        {w.items.map((i) => (
          <div key={i.process} className="rounded-xl border border-gray-100 bg-white px-3 py-2.5 mb-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-text">Classe {i.cls}</span>
              <span className="font-mono text-text/60 text-xs">{i.process}</span>
              <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-gray-100 text-text/80">{i.stage_pt}{i.stage_since ? ` desde ${dBr(i.stage_since)}` : ''}</span>
            </div>
            <div className="mt-2 space-y-1">
              {i.deadlines.map((d) => (
                <div key={d.key} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-text/70">{d.pt}:</span><b className="text-text">{dBr(d.date)}</b>
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${dayTone(d.days)}`}>{dayText(d.days)}</span>
                </div>
              ))}
              {!i.grant_date && <div className="text-[11px] text-text/50">Renovação (10 anos) e uso obrigatório (5 anos) passam a contar da concessão.</div>}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-2">Colidências encontradas pela vigilância</div>
        {!w.conflicts.length && <div className="text-sm text-text/50">Nenhuma marca parecida encontrada até agora.</div>}
        {w.conflicts.map((c) => (
          <div key={`${c.rpi}-${c.new_process}`} className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2 mb-1.5 text-sm">
            <div className="text-text"><b>{c.new_mark}</b> · processo {c.new_process} · classe {c.new_classes}{c.same_class ? ' (mesma classe)' : ''}</div>
            <div className="text-xs text-text/60">{c.new_owner} · {c.reason} · RPI {c.rpi}{c.opposition_deadline ? ` · oposição até ${dBr(c.opposition_deadline)}` : ''}{c.status ? ` · ${c.status}` : ''}</div>
          </div>
        ))}
      </div>
      {w.events.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-2">Despachos do INPI</div>
          {w.events.map((e, k) => (
            <div key={k} className="text-sm text-text mb-1"><span className="font-mono text-xs text-text/60">{e.process}</span> · RPI {e.rpi} ({dBr(e.date)}) · {e.code} {e.name}{e.deadline ? ` · prazo ${dBr(e.deadline)}` : ''}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// Documentos do pedido: protocolo, procuração, logo, certificado, comprovantes. A equipe abre e anexa.
const DOC_KINDS = [['protocolo', 'Protocolo do INPI'], ['poa_signed', 'Procuração assinada'], ['logo', 'Logo'], ['nota_fiscal', 'Nota fiscal'], ['certificado', 'Certificado de registro'], ['rpi', 'Recorte / print da publicação'], ['comprovante_gru', 'Comprovante da GRU'], ['payment_proof', 'Comprovante da transferência (cliente)'], ['other', 'Outro documento']];
const DocsBlock = ({ o, onUpload, busy }) => {
  const [kind, setKind] = useState('protocolo');
  const [label, setLabel] = useState('');
  const [client, setClient] = useState(true);
  const ref = useRef(null);
  const files = o.files || [];
  const send = async () => {
    const f = ref.current && ref.current.files && ref.current.files[0];
    if (!f) return;
    const ok = await onUpload(o, f, { kind, label, client: kind === 'comprovante_gru' ? false : client });
    if (ok) { setLabel(''); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div className="mb-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-2">Documentos</div>
      {!files.length && <div className="text-sm text-text/50 mb-2">Nenhum documento ainda.</div>}
      <div className="space-y-1.5 mb-3">
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-text truncate">{f.label || f.kind}</div>
              <div className="text-xs text-text/50 truncate">{f.name} · {fmt(f.at)}{f.by ? ` · ${f.by}` : ''}{f.client === false ? ' · só a equipe vê' : ''}</div>
            </div>
            {f.url
              ? <a href={f.url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-semibold bg-text text-white rounded-lg px-3 py-1.5 hover:opacity-90">Abrir</a>
              : <span className="shrink-0 text-[11px] text-text/50">no e-mail da equipe</span>}
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-dashed border-gray-200 p-3 space-y-2">
        <div className="text-xs font-semibold text-text/70">Anexar documento</div>
        <div className="flex flex-wrap gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm bg-white">
            {DOC_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Descrição (opcional)" className="flex-1 min-w-[140px] rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
        </div>
        <input ref={ref} type="file" accept="application/pdf,image/*" className="block w-full text-sm" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-text/70"><input type="checkbox" checked={kind === 'comprovante_gru' ? false : client} disabled={kind === 'comprovante_gru'} onChange={(e) => setClient(e.target.checked)} /> O cliente também vê na área dele</label>
          <button type="button" disabled={busy} onClick={send} className="text-sm font-semibold bg-text text-white rounded-lg px-3 py-1.5 disabled:opacity-50">Anexar</button>
        </div>
        <div className="text-[11px] text-text/50">PDF ou imagem, até 3 MB.</div>
      </div>
    </div>
  );
};

// Cadastro do cliente, editável pela equipe
const FIELDS_TOP = [['customer_name', 'Cliente (nome)'], ['customer_email', 'E-mail do cliente'], ['phone', 'Telefone'], ['address', 'Endereço de cobrança'], ['trademark', 'Marca(s)'], ['classes', 'Classes'], ['owner', 'Titular (resumo)']];
const FIELDS_D = [['owner_name', 'Titular(es)'], ['owner_id_no', 'Documento / nº INPI'], ['owner_state', 'Nacionalidade / estado'], ['owner_country', 'País do titular'], ['addr_street', 'Endereço do titular'], ['addr_city', 'Cidade'], ['addr_region', 'Estado / região'], ['addr_postal', 'CEP / código postal'], ['addr_country', 'País do endereço'], ['mark_text', 'Elemento nominativo'], ['mark_translation', 'Tradução'], ['contact_name', 'Contato (nome)'], ['contact_email', 'Contato (e-mail)'], ['cc_emails', 'Outros e-mails que recebem os avisos (separe com vírgula, até 5)'], ['client_ref', 'Ref. do cliente'], ['notes', 'Observações']];
const EditCadastro = ({ o, onAction, busy, onClose }) => {
  const d = o.details || {};
  const classList = String(o.classes || '').split(/[^0-9]+/).filter(Boolean).map((x) => String(Number(x)));
  const [top, setTop] = useState(() => Object.fromEntries(FIELDS_TOP.map(([k]) => [k, o[k] || ''])));
  const [det, setDet] = useState(() => ({ ...Object.fromEntries(FIELDS_D.map(([k]) => [k, d[k] || ''])), owner_type: d.owner_type || '', mark_type: d.mark_type || '' }));
  const [goods, setGoods] = useState(() => ({ ...(d.goods || {}) }));
  const inp = 'w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm bg-white';
  const save = async () => {
    await onAction(o, 'cadastro', { cadastro: JSON.stringify({ ...top, details: { ...det, goods } }) });
    onClose();
  };
  const goodsKeys = [...new Set([...classList, ...Object.keys(goods).filter((k) => k !== 'all')])];
  return (
    <div className="mb-4 rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="text-sm font-bold text-text">Editar cadastro do cliente</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {FIELDS_TOP.map(([k, l]) => <label key={k} className="text-xs text-text/60">{l}<input value={top[k]} onChange={(e) => setTop({ ...top, [k]: e.target.value })} className={inp} /></label>)}
        <label className="text-xs text-text/60">Tipo de titular
          <select value={det.owner_type} onChange={(e) => setDet({ ...det, owner_type: e.target.value })} className={inp}><option value="">—</option><option value="individual">Pessoa física</option><option value="company">Empresa</option></select>
        </label>
        <label className="text-xs text-text/60">Apresentação da marca
          <select value={det.mark_type} onChange={(e) => setDet({ ...det, mark_type: e.target.value })} className={inp}><option value="">—</option><option value="word">Nominativa</option><option value="mixed">Mista</option><option value="figurative">Figurativa</option><option value="3d">Tridimensional</option></select>
        </label>
        {FIELDS_D.map(([k, l]) => <label key={k} className="text-xs text-text/60">{l}<input value={det[k]} onChange={(e) => setDet({ ...det, [k]: e.target.value })} className={inp} /></label>)}
      </div>
      {goodsKeys.map((c) => (
        <label key={c} className="block text-xs text-text/60">Especificação da classe {c}
          <textarea rows={3} value={goods[c] || ''} onChange={(e) => setGoods({ ...goods, [c]: e.target.value })} className={inp} />
        </label>
      ))}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="text-sm font-semibold rounded-lg px-3 py-1.5 bg-background-auxiliary text-text">Cancelar</button>
        <button type="button" disabled={busy} onClick={save} className="text-sm font-semibold rounded-lg px-3 py-1.5 bg-text text-white disabled:opacity-50">Salvar cadastro</button>
      </div>
    </div>
  );
};

const WHO_TONE = { Gabriele: 'bg-violet-100 text-violet-800', Carol: 'bg-sky-100 text-sky-800', Equipe: 'bg-gray-100 text-gray-700' };
const fmtDay = (s) => (s ? s.split('-').reverse().join('/') : '');

// One service order: the steps in order, the next one open with its form.
// o que o sistema leu de um protocolo do e-Marcas, com os alertas da conferência
const ProtocoloLido = ({ x }) => (
  <div className={`rounded-xl border px-3 py-2.5 text-xs ${x.warnings.length ? 'border-red-200 bg-red-50' : 'border-primary/25 bg-primary/5'}`}>
    <div className="font-semibold text-text text-sm mb-1 flex items-center gap-1.5"><Icon name={x.warnings.length ? 'alert' : 'check'} size={15} className={x.warnings.length ? 'text-red-700' : 'text-primary-dark'} />{x.parsed.ok ? `Processo ${x.parsed.process}` : x.name}</div>
    {x.parsed.ok && <div className="text-text/75">{[x.parsed.mark, x.parsed.cls && `classe ${x.parsed.cls}`, x.parsed.presentation, x.parsed.date && `protocolado em ${x.parsed.date}${x.parsed.time ? ` às ${x.parsed.time}` : ''}`, x.parsed.petition && `protocolo ${x.parsed.petition}`].filter(Boolean).join(' · ')}</div>}
    {x.parsed.owner && <div className="text-text/60">Titular: {x.parsed.owner}</div>}
    {x.parsed.spec && <div className="text-text/60">Especificação: {x.parsed.spec}</div>}
    {x.warnings.length ? <ul className="mt-1.5 space-y-0.5 text-red-700 font-semibold">{x.warnings.map((w) => <li key={w}>{w}</li>)}</ul> : <div className="mt-1 font-semibold text-primary-dark">Confere com o pedido: titular, marca e classe.</div>}
  </div>
);
const OSCard = ({ item, onAction, onUpload, busy }) => {
  const { os } = item;
  const o = { session_id: item.session_id };
  const next = (os.steps || []).find((s) => !s.done_at);
  const [f, setF] = useState({});
  const [lidos, setLidos] = useState([]);
  const lerProtocolo = async (file) => {
    if (!file || !onUpload) return;
    const j = await onUpload(o, file, { kind: 'protocolo', label: '', client: true });
    if (!j) return;
    const parsed = j.parsed || { ok: false };
    setLidos((l) => [...l.filter((x) => !(parsed.ok && x.parsed.process === parsed.process)), { name: file.name, parsed, warnings: j.warnings || [] }]);
    if (parsed.ok) {
      const line = `${parsed.process} ${String(parsed.mark || item.mark || '').toUpperCase()} ${parsed.cls || ''}`.trim();
      setF((cur) => { const lines = String(cur.p || '').split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith(parsed.process)); return { ...cur, p: [...lines, line].join('\n') }; });
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = (step, extra = {}, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    onAction(o, 'os_step', { os: os.id, step, ...extra });
    setF({});
  };
  const input = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm';
  const btn = 'bg-primary text-white font-semibold rounded-xl px-4 py-2.5 text-sm hover:bg-primary-hover disabled:opacity-40';
  return (
    <div className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-4 md:p-5">
      <div className="flex flex-wrap items-start gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-text">{os.title} · {item.mark || item.ref}</div>
          <div className="text-xs text-text/60">{item.customer} · {item.ref} · {os.id} · aberta {fmt(os.opened_at)}</div>
          {os.note && <div className="text-xs text-text/70 mt-1">Obs.: {os.note}</div>}
        </div>
        {item.is_test && <span className="text-[11px] font-semibold bg-amber-50 text-amber-800 rounded-full px-2 py-0.5">TESTE</span>}
      </div>
      <ol className="space-y-2">
        {os.steps.map((st, i) => {
          const isNext = next && next.key === st.key;
          return (
            <li key={st.key} id={`os-${os.id}-${st.key}`} className={`rounded-xl px-3 py-2.5 transition-shadow ${isNext ? 'bg-primary/5 border border-primary/30' : 'bg-gray-50'}`}>
              <div className="flex items-start gap-3">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${st.done_at ? 'bg-primary text-white' : isNext ? 'bg-white border-2 border-primary text-primary' : 'bg-white border border-gray-200 text-gray-400'}`}>{st.done_at ? '✓' : i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${st.done_at ? 'text-text/70' : 'text-text font-semibold'}`}>
                    {st.label} <span className={`ml-1 text-[10px] font-semibold rounded px-1.5 py-0.5 align-middle ${WHO_TONE[st.who] || WHO_TONE.Equipe}`}>{st.who}</span>
                  </div>
                  {st.done_at && (
                    <div className="text-xs text-text/50">
                      {st.done_by} · {fmt(st.done_at)}{st.info ? ` · ${st.info}` : ''}
                      {st.key !== 'protocolo' && <button type="button" disabled={busy} onClick={() => window.confirm('Desfazer este passo?') && onAction(o, 'os_undo', { os: os.id, step: st.key })} className="ml-2 underline hover:text-text">desfazer</button>}
                    </div>
                  )}
                  {st.key === 'pagamento' && os.gru && os.gru.receipt && <a href={os.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary-dark hover:underline">Comprovante: {os.gru.receipt.name}</a>}
                  {isNext && (
                    <div className="mt-3 space-y-2">
                      {st.key === 'gru' && (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <input className={input} placeholder="Número da GRU" value={f.n || ''} onChange={set('n')} />
                            <input className={input} placeholder="Valor (R$)" inputMode="decimal" value={f.v || ''} onChange={set('v')} />
                            <input className={input} type="date" title="Vencimento" value={f.d || ''} onChange={set('d')} />
                          </div>
                          <button type="button" disabled={busy || !f.n || !f.v} onClick={() => run('gru', { gru_number: f.n, gru_value: f.v, gru_due: f.d || '' })} className={btn}>Emitida: avisar a Gabriele para pagar</button>
                        </>
                      )}
                      {st.key === 'pagamento' && (
                        <>
                          <p className="text-xs text-text/70">A Gabriele recebeu o e-mail com a GRU {os.gru ? `${os.gru.number} (R$ ${Number(os.gru.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}${os.gru.due ? `, vence ${fmtDay(os.gru.due)}` : ''})` : ''}. Quando ela confirmar o pagamento, este passo fica pronto sozinho.</p>
                          <button type="button" disabled={busy} onClick={() => run('pagamento', {}, 'Marcar a GRU como paga sem o comprovante da Gabriele?')} className="text-sm font-semibold text-primary-dark hover:underline disabled:opacity-40">Já está paga: marcar aqui</button>
                        </>
                      )}
                      {st.key === 'protocolo' && os.type === 'registro' && (
                        <>
                          {onUpload && (
                            <label className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-dashed border-primary/30 bg-white px-3 py-3 cursor-pointer hover:bg-primary/5">
                              <input type="file" accept="application/pdf" className="sr-only peer" disabled={busy} onChange={(e) => { const fl = e.target.files && e.target.files[0]; e.target.value = ''; lerProtocolo(fl); }} />
                              <span className="inline-flex items-center gap-2 rounded-lg bg-text text-white px-3.5 py-2 text-sm font-semibold peer-focus-visible:ring-2 peer-focus-visible:ring-primary"><Icon name="upload" size={15} />{busy ? 'Lendo…' : 'Subir o PDF do protocolo'}</span>
                              <span className="text-xs text-text/60 flex-1 min-w-[180px]">O PDF que o e-Marcas gera. O sistema lê o processo, a data, a marca e a classe, confere com o pedido e guarda o arquivo para o cliente. Um PDF por classe.</span>
                            </label>
                          )}
                          {lidos.map((x) => <ProtocoloLido key={x.parsed.process || x.name} x={x} />)}
                          <p className="text-xs text-text/60">{lidos.some((x) => x.parsed.ok) ? 'Linha preenchida pelo PDF (pode corrigir):' : 'Ou digite uma linha por processo: número, marca e classe. Ex.: 930123456 PLYSE 9'}</p>
                          <textarea rows={3} className={`${input} font-mono`} value={f.p || ''} onChange={set('p')} />
                          <button type="button" disabled={busy || !(f.p || '').trim()} onClick={() => { const warn = lidos.some((x) => x.warnings.length); run('protocolo', { filings: f.p, meta: JSON.stringify(lidos.filter((x) => x.parsed.ok).map((x) => ({ process: x.parsed.process, filed_at: x.parsed.filed_at, petition: x.parsed.petition, presentation: x.parsed.presentation }))) }, warn ? 'O PDF tem alertas em vermelho. Registrar mesmo assim e avisar o cliente?' : 'Registrar o protocolo e avisar o cliente por e-mail agora?'); setLidos([]); }} className={btn}>Registrar protocolo e avisar o cliente</button>
                        </>
                      )}
                      {st.key === 'protocolo' && os.type !== 'registro' && (
                        <>
                          <input className={input} placeholder="Número da petição" value={f.p || ''} onChange={set('p')} />
                          <button type="button" disabled={busy || !(f.p || '').trim()} onClick={() => run('protocolo', { petition: f.p }, 'Registrar o protocolo e avisar o cliente por e-mail agora?')} className={btn}>Registrar protocolo e avisar o cliente</button>
                        </>
                      )}
                      {st.key === 'nf' && (
                        <>
                          <input className={input} placeholder="Número da nota fiscal" value={f.nf || ''} onChange={set('nf')} />
                          <div className="flex flex-wrap items-center gap-2">
                            <button type="button" disabled={busy || !(f.nf || '').trim()} onClick={() => run('nf', { nf: f.nf })} className={btn}>Nota fiscal emitida</button>
                            <button type="button" disabled={busy} onClick={() => run('nf', { skip: 'true' }, 'Seguir sem emitir nota fiscal? A etapa fica marcada como "Sem nota fiscal".')} className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-text hover:bg-gray-50 disabled:opacity-40">Sem nota fiscal: seguir</button>
                          </div>
                        </>
                      )}
                      {['conferir', 'cadastro', 'redigir'].includes(st.key) && (
                        <div className="flex flex-wrap gap-2">
                          <input className={`${input} flex-1 min-w-[160px]`} placeholder="Observação (opcional)" value={f.i || ''} onChange={set('i')} />
                          <button type="button" disabled={busy} onClick={() => run(st.key, { info: f.i || '' })} className={btn}>Feito</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      {!os.steps.some((s) => s.key === 'protocolo' && s.done_at) && (
        <button type="button" disabled={busy} onClick={() => { const why = window.prompt('Motivo do cancelamento da OS:'); if (why !== null) onAction(o, 'os_cancel', { os: os.id, note: why }); }} className="mt-3 text-xs text-text/50 hover:text-red-700 underline">Cancelar esta OS</button>
      )}
    </div>
  );
};

// A client message: Portuguese translation for the team, reply box in Portuguese.
const MsgCard = ({ item, onAction, busy }) => {
  const { msg } = item;
  const [reply, setReply] = useState('');
  const waiting = msg.status === 'aguardando_traducao';
  return (
    <div className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-4 md:p-5">
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-text">{item.customer} · {item.mark || item.ref}</div>
          <div className="text-xs text-text/60">{item.ref} · {fmt(msg.at)} · {msg.via === 'email' ? 'por e-mail' : 'pela área do cliente'}</div>
        </div>
        {item.is_test && <span className="text-[11px] font-semibold bg-amber-50 text-amber-800 rounded-full px-2 py-0.5">TESTE</span>}
      </div>
      {msg.text_pt
        ? <div className="bg-primary/5 border-l-4 border-primary rounded-lg px-3 py-2 text-sm text-text whitespace-pre-line mb-2">{msg.text_pt}</div>
        : <div className="bg-amber-50 text-amber-800 rounded-lg px-3 py-2 text-sm mb-2">A tradução chega na próxima rodada da Duda (perto de 12h e 17h, de segunda a sexta).</div>}
      <details className="mb-3"><summary className="text-xs text-text/50 cursor-pointer">Original em inglês</summary><div className="text-xs text-text/70 whitespace-pre-line mt-1">{msg.text_en}</div></details>
      {waiting ? (
        <div className="text-sm text-text/70 bg-gray-50 rounded-lg px-3 py-2">Resposta de {msg.reply_by}: “{msg.reply_pt}”. Sai em inglês para o cliente na próxima rodada da Duda.</div>
      ) : (
        <>
          <label className="block text-sm font-semibold text-text mb-1" htmlFor={`r-${msg.id}`}>Resposta em português</label>
          <textarea id={`r-${msg.id}`} rows={3} value={reply} onChange={(e) => setReply(e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="Escreva em português. O cliente recebe em inglês." />
          <button type="button" disabled={busy || !reply.trim()} onClick={() => { onAction({ session_id: item.session_id }, 'msg_reply', { msg: msg.id, reply }); setReply(''); }} className="mt-2 bg-primary text-white font-semibold rounded-xl px-4 py-2.5 text-sm hover:bg-primary-hover disabled:opacity-40">Enviar resposta</button>
        </>
      )}
    </div>
  );
};

const NewOS = ({ o, types, onAction, busy }) => {
  const [type, setType] = useState('manifestacao');
  const [note, setNote] = useState('');
  return (
    <div>
      <div className="text-sm font-semibold text-text mb-1">Nova ordem de serviço</div>
      <div className="flex flex-wrap gap-2">
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 text-sm">
          {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-w-[160px] rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="Observação (prazo, processo, o que o cliente contratou)" />
        <button type="button" disabled={busy} onClick={() => { if (window.confirm('Abrir esta ordem de serviço? A equipe recebe o aviso por e-mail.')) { onAction(o, 'os_create', { type, note }); setNote(''); } }} className="bg-text text-white font-semibold rounded-xl px-4 py-2 text-sm hover:opacity-90 disabled:opacity-40">Abrir OS</button>
      </div>
    </div>
  );
};

// logo da marca (o que o cliente enviou) ou as iniciais, para achar o cliente de olho
const MarkAvatar = ({ o, big }) => {
  const [bad, setBad] = useState(false);
  const name = (o.marks && o.marks[0]) || o.trademark || '?';
  const letters = name.replace(/[^A-Za-z0-9À-ÿ ]/g, '').trim().slice(0, 2).toUpperCase() || '?';
  const size = big ? 'w-28 h-28' : 'w-12 h-12';
  if (o.logo_url && !bad) return <img src={o.logo_url} alt={`Logo ${name}`} onError={() => setBad(true)} className={`${size} rounded-xl object-contain bg-white border border-gray-100 p-1 shrink-0`} />;
  if (big) return null;
  return <div className={`${size} rounded-xl bg-primary/10 text-primary-dark font-bold text-lg flex items-center justify-center shrink-0`} aria-hidden="true">{letters}</div>;
};

// ---------- painel em lista: uma linha por processo, agrupada por etapa ----------
const FUNNEL = [
  ['cliente', 'Esperando cliente', 'user'],
  ['conferir', 'Conferir e cadastrar', 'checkSquare'],
  ['gru', 'GRU', 'barcode'],
  ['protocolar', 'Protocolar', 'landmark'],
  ['nf', 'Nota fiscal', 'receipt'],
  ['publicacao', 'Esperando publicação', 'clock'],
  ['oposicao', 'Em oposição', 'shield'],
  ['exame', 'Em exame', 'search'],
  ['exigencia', 'Exigência ou oposição', 'alert'],
  ['concedido', 'Concedidos', 'award'],
];
const GROUP_OF_STEP = { conferir: 'conferir', cadastro: 'conferir', redigir: 'conferir', gru: 'gru', pagamento: 'gru', protocolo: 'protocolar', nf: 'nf' };
const GROUP_OF_WATCH = { filed: 'publicacao', formal_requirement: 'exigencia', opposition_window: 'oposicao', opposition_filed: 'exigencia', exam: 'exame', suspended: 'exame', office_action: 'exigencia', refused: 'exigencia', refused_final: 'exigencia', appeal_notified: 'exigencia', allowed: 'concedido', registered: 'concedido' };
const RANK_W = { filed: 0, formal_requirement: 0, opposition_window: 1, opposition_filed: 1, exam: 2, office_action: 2, suspended: 2, appeal_notified: 2, refused: 2, refused_final: 2, allowed: 3, registered: 4 };
const ROBOT = 'Robô Rita';
const daysSince = (s) => { const t = Date.parse(s); return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((Date.now() - t) / 864e5)); };
const ddmm = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().slice(0, 2).join('/') : '');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// régua dos lembretes automáticos ao cliente (igual à do robô Ivo): dia 2, 4 e 9 depois do pagamento, depois toda semana; no 4º a equipe entra
const REMIND_DAYS = [2, 4, 9];
const ESCALATE_AT = 4;
const remindDay = (n) => (n <= REMIND_DAYS.length ? REMIND_DAYS[n - 1] : REMIND_DAYS[REMIND_DAYS.length - 1] + 7 * (n - REMIND_DAYS.length));
const remindersOf = (o) => (Array.isArray(o.reminders) ? o.reminders : []);
const startOf = (o) => o.paid_at || (o.invoice && o.invoice.sent_at) || '';
const nextRemind = (o) => {
  if (o.next_reminder) return o.next_reminder;
  const r = remindersOf(o);
  let t = (Date.parse(startOf(o)) || Date.now()) + remindDay(r.length + 1) * 864e5;
  const last = r.length ? Date.parse(r[r.length - 1].at) : 0;
  if (last && t < last + 2 * 864e5) t = last + 2 * 864e5;
  return new Date(Math.max(t, Date.now())).toISOString();
};
const MISS_ROW = { payment: 'comprovante', owner: 'dados', mark: 'dados', goods: 'dados', logo: 'logo', poa: 'procuração' };
const faltaOf = (o) => {
  const ck = o.checklist || {};
  const keys = Array.isArray(o.missing) ? o.missing : [!ck.details && 'owner', !ck.poa && 'poa'].filter(Boolean);
  const words = keys.map((k) => MISS_ROW[k] || k).filter((w, i, a) => a.indexOf(w) === i);
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} e ${words[words.length - 1]}` : (words[0] || 'dados');
};

// monta as linhas do painel a partir dos pedidos e das ordens de serviço abertas
const rowsOf = (orders, osOpen) => {
  const rows = [];
  for (const o of orders) {
    const oss = osOpen.filter((x) => x.session_id === o.session_id);
    const ck = o.checklist || {};
    const base = { o, key: o.session_id, mark: (o.marks && o.marks[0]) || o.trademark || '(sem marca)', customer: o.customer_name || o.customer_email || '', test: o.is_test };
    const waitingClient = ['paid', 'poa_sent'].includes(o.stage) || (o.stage === 'poa_received' && !oss.length && (!ck.details || (o.missing || []).includes('payment')));
    if (waitingClient) {
      const since = startOf(o);
      const rem = remindersOf(o); const esc = rem.length >= ESCALATE_AT;
      const last = rem[rem.length - 1];
      const auto = rem.length ? `${rem.length}º lembrete em ${ddmm(last.at)} · próximo ${ddmm(nextRemind(o))}` : `1º lembrete em ${ddmm(nextRemind(o))}`;
      rows.push({ ...base, key: `${o.session_id}:cliente`, group: 'cliente', where: `Classe ${o.classes || '?'} · a protocolar`, stage: `Falta ${faltaOf(o)}`, when: `desde ${ddmm(since)} · ${plural(daysSince(since), 'dia', 'dias')}`,
        urgent: esc, who: esc ? 'Belle' : 'Robô Ivo', todo: esc, action: esc ? 'cobrar' : 'auto', auto, note: esc ? `o sistema já lembrou ${rem.length} vezes sem resposta: vale um contato pessoal` : '' });
    }
    if (o.payment_status === 'proof_received') {
      const pr = [...(o.timeline || [])].reverse().find((e) => e.stage === 'payment_proof');
      rows.push({ ...base, key: `${o.session_id}:credito`, group: 'conferir', where: `Invoice ${(o.invoice && o.invoice.number) || o.ref}`, stage: 'Conferir o crédito da transferência', when: `comprovante em ${ddmm((pr && pr.at) || o.paid_at)}`, urgent: true, who: 'Carol', todo: true, action: 'credito', note: 'confira na conta antes de pagar a GRU' });
    }
    if (!waitingClient && o.stage === 'poa_received' && !oss.length) {
      rows.push({ ...base, key: `${o.session_id}:conferir`, group: 'conferir', where: `Classe ${o.classes || '?'}`, stage: 'Dados e procuração completos', when: 'a OS de registro abre sozinha', who: 'Belle / Carol', todo: false });
    }
    for (const x of oss) {
      const next = (x.os.steps || []).find((s) => !s.done_at);
      if (!next) continue;
      const prev = (x.os.steps || []).filter((s) => s.done_at).map((s) => s.done_at).sort().pop() || x.os.opened_at;
      rows.push({ ...base, key: `${o.session_id}:${x.os.id}`, group: GROUP_OF_STEP[next.key] || 'conferir', where: `${x.os.title}${o.classes ? ` · classe ${o.classes}` : ''}`, stage: next.label, when: daysSince(prev) === 0 ? 'desde hoje' : `há ${plural(daysSince(prev), 'dia', 'dias')}`, urgent: daysSince(prev) >= 2, who: next.who === 'Equipe' ? 'Belle / Carol' : next.who, todo: true, action: 'os', os: x });
    }
    if (['filed', 'watch'].includes(o.stage)) {
      const items = (o.watch && o.watch.items) || [];
      const filings = o.filings && o.filings.length ? o.filings : items.map((i) => ({ process: i.process, cls: i.cls }));
      for (const p of filings) {
        const it = items.find((i) => String(i.process) === String(p.process));
        const opp = it && (it.deadlines || []).find((d) => d.key === 'opposition');
        let group = it ? (GROUP_OF_WATCH[it.stage] || 'publicacao') : (p.rpi ? 'oposicao' : 'publicacao');
        if (!it && p.opp_until) group = 'oposicao';
        const hasCert = (o.files || []).some((f) => f.kind === 'certificado');
        const act = group === 'exigencia' ? 'abrir_os' : group === 'concedido' && !hasCert ? 'certificado' : 'ver';
        const when = opp && opp.days >= 0 ? `até ${ddmm(opp.date)} · ${plural(opp.days, 'dia', 'dias')}` : it && it.stage_since ? `desde ${ddmm(it.stage_since)}` : p.filed_at ? `protocolado ${String(p.filed_at).slice(0, 5)}` : '';
        rows.push({ ...base, key: `${o.session_id}:${p.process}`, group, process: String(p.process), cls: String(p.cls || ''), where: `Classe ${p.cls || '?'} · ${p.process}`, stage: it ? it.stage_pt : p.rpi ? `Publicado na RPI ${p.rpi}` : 'Protocolado', when, urgent: !!(opp && opp.days <= 7), who: act === 'ver' ? ROBOT : 'Belle / Carol', todo: act !== 'ver', action: act, item: it, filing: p });
      }
    }
  }
  return rows;
};

// as 11 etapas de um pedido, para a linha aberta
const stepsOf = (o, osOpen) => {
  const ck = o.checklist || {};
  const reg = osOpen.find((x) => x.session_id === o.session_id && x.os.type === 'registro');
  const done = (k) => !!(reg && (reg.os.steps || []).find((s) => s.key === k && s.done_at));
  const items = (o.watch && o.watch.items) || [];
  const filed = !!((o.filings && o.filings.length) || items.length || ['filed', 'watch'].includes(o.stage));
  const ranks = items.map((i) => (RANK_W[i.stage] != null ? RANK_W[i.stage] : 0));
  const min = ranks.length ? Math.min(...ranks) : filed ? 0 : -1;
  const nfDone = done('nf') || (o.files || []).some((f) => f.kind === 'nota_fiscal');
  const list = [
    ['Pagamento', !!o.paid_at, o.paid_at ? ddmm(o.paid_at) : ''],
    ['Procuração', !!ck.poa || filed, ''],
    ['Dados', !!ck.details || filed, ''],
    ['Cadastro e-INPI', done('cadastro') || filed, ''],
    ['GRU', done('pagamento') || filed, ''],
    ['Protocolo', done('protocolo') || filed, ''],
    ['Nota fiscal', nfDone, reg && !nfDone && done('protocolo') ? 'falta' : ''],
    ['Publicação', min >= 1, ''],
    ['Oposição', min >= 2, ''],
    ['Exame', min >= 3, ''],
    ['Concessão', min >= 4, ''],
  ];
  const cur = list.findIndex((s) => !s[1] && s[0] !== 'Nota fiscal');
  return list.map(([label, ok, note], i) => ({ label, ok, note, current: i === cur, warn: label === 'Nota fiscal' && note === 'falta' }));
};

const Steps = ({ steps }) => (
  <ol className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-11 gap-2" aria-label="Etapas do processo">
    {steps.map((s) => (
      <li key={s.label} className="flex flex-col items-center text-center gap-1 text-[11px]">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${s.ok ? 'bg-primary text-text' : s.warn ? 'bg-amber-50 border-2 border-amber-400' : s.current ? 'bg-white border-[3px] border-primary' : 'border-2 border-dashed border-gray-300'}`}>{s.ok && <Icon name="check" size={15} strokeWidth={3} />}</span>
        <span className={`font-semibold leading-tight ${s.warn ? 'text-amber-800' : s.ok || s.current ? 'text-text' : 'text-text/50'}`}>{s.label}</span>
        {(s.note || s.current) && <span className={`leading-tight ${s.warn ? 'text-amber-800 font-semibold' : 'text-text/55'}`}>{s.note || 'agora'}</span>}
      </li>
    ))}
  </ol>
);

const QuickBtn = ({ icon, children, onClick, active }) => (
  <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3 text-sm font-semibold border ${active ? 'bg-text text-white border-text' : 'bg-white text-text border-gray-200 hover:bg-gray-50'}`}><Icon name={icon} size={15} />{children}</button>
);

// o conteúdo da linha aberta: etapas, dados, documentos e ações rápidas
const RowDetail = ({ r, osOpen, onAction, onUpload, busy, types, onOpenFull }) => {
  const { o } = r;
  const [panel, setPanel] = useState(r.action === 'certificado' ? 'docs' : r.action === 'abrir_os' ? 'os' : '');
  const [note, setNote] = useState('');
  const d = o.details || {};
  const emails = [o.customer_email, d.contact_email, ...String(d.cc_emails || '').split(/[,;\s]+/)].filter((x, i, a) => x && /@/.test(x) && a.indexOf(x) === i);
  const conflicts = (o.watch && o.watch.conflicts) || [];
  const files = o.files || [];
  const last = [...(o.timeline || [])].reverse().find((e) => e.stage === 'note');
  const toggle = (p) => setPanel(panel === p ? '' : p);
  return (
    <div className="rounded-2xl border border-gray-100 bg-background-white p-4 md:p-5 space-y-4">
      <Steps steps={stepsOf(o, osOpen)} />
      {r.os && <OSCard item={r.os} onAction={onAction} onUpload={onUpload} busy={busy} />}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-text/50">Cliente</div>
          <div className="text-text">{o.owner || o.customer_name}</div>
          {emails.map((e) => <div key={e} className="text-text/70 break-all">{e}</div>)}
          {r.filing && r.filing.protocol && <div className="text-text/70">Protocolo {r.filing.protocol}{r.filing.rpi ? ` · RPI ${r.filing.rpi}` : ''}</div>}
          {o.watch && o.watch.active && <div className="text-text/70">Colidências: {conflicts.length}</div>}
        </div>
        <div className="space-y-1.5">
          <div className="text-xs font-bold uppercase tracking-wider text-text/50">Documentos</div>
          <div className="flex flex-wrap gap-1.5">
            {files.map((f) => (f.url
              ? <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 min-h-[36px] rounded-xl bg-primary/10 px-2.5 text-xs font-semibold text-primary-dark hover:bg-primary/20"><Icon name="download" size={14} />{f.label || f.kind}</a>
              : <span key={f.id} className="inline-flex items-center min-h-[36px] rounded-xl bg-gray-50 px-2.5 text-xs text-text/60">{f.label || f.kind}</span>))}
            {!files.length && <span className="text-text/50 text-xs">Nenhum documento ainda.</span>}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-text/50">Última observação</div>
          <div className="bg-gray-50 rounded-xl px-3 py-2 text-text/80 whitespace-pre-line">{last ? `${last.text} · ${fmt(last.at)}` : (o.team_notes ? o.team_notes.split('\n')[0] : 'Nenhuma ainda.')}</div>
        </div>
      </div>
      {r.group === 'cliente' && (
        <div className="rounded-xl bg-primary/5 border border-primary/15 px-3 py-2.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="inline-flex items-center gap-1.5 font-semibold text-primary-dark"><Icon name="refresh" size={15} />Lembretes automáticos ao cliente</span>
          <span className="text-text/75">{remindersOf(o).length ? remindersOf(o).map((x, i) => `${i + 1}º ${ddmm(x.at)}${x.manual ? ' (manual)' : ''}`).join(' · ') : 'nenhum ainda'}</span>
          <span className="text-text/75">· próximo {ddmm(nextRemind(o))}</span>
          <span className="text-text/50 text-xs basis-full">Régua: dia 2, 4 e 9 depois do pagamento e depois toda semana, até completar. No 4º lembrete sem resposta, o pedido sobe para a equipe.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
        <span className="text-xs font-bold uppercase tracking-wider text-text/50 mr-1">Ações rápidas</span>
        {r.group === 'cliente' && <QuickBtn icon="send" onClick={() => { if (window.confirm(`Mandar agora para ${o.customer_email} o lembrete com o que falta?`)) onAction(o, 'remind'); }}>Lembrar agora</QuickBtn>}
        <QuickBtn icon="mail" onClick={() => toggle('edit')} active={panel === 'edit'}>Adicionar ou trocar e-mail</QuickBtn>
        <QuickBtn icon="pencil" onClick={() => toggle('edit')} active={panel === 'edit'}>Trocar dados</QuickBtn>
        <QuickBtn icon="message" onClick={() => toggle('note')} active={panel === 'note'}>Observação</QuickBtn>
        <QuickBtn icon="clip" onClick={() => toggle('docs')} active={panel === 'docs'}>Anexar documento</QuickBtn>
        <QuickBtn icon="plus" onClick={() => toggle('os')} active={panel === 'os'}>Abrir OS</QuickBtn>
        {o.client_url && <QuickBtn icon="send" onClick={() => { if (window.confirm(`Reenviar o e-mail de acesso para ${o.customer_email}?`)) onAction(o, 'resend_access'); }}>Reenviar acesso ao cliente</QuickBtn>}
        {o.stage === 'poa_sent' && <QuickBtn icon="docSign" onClick={() => { if (window.confirm('Marcar a procuração como recebida?')) onAction(o, 'poa_received'); }}>Procuração recebida</QuickBtn>}
        {o.poa_url && !['filed', 'watch'].includes(o.stage) && <a href={o.poa_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3 text-sm font-semibold border bg-white text-text border-gray-200 hover:bg-gray-50"><Icon name="download" size={15} />Procuração (PDF)</a>}
        <button type="button" onClick={() => onOpenFull(o.session_id)} className="ml-auto inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-4 text-sm font-semibold bg-text text-white hover:opacity-90">Abrir o processo completo<Icon name="right" size={15} /></button>
      </div>
      {panel === 'edit' && <EditCadastro o={o} onAction={onAction} busy={busy} onClose={() => setPanel('')} />}
      {panel === 'docs' && <DocsBlock o={o} onUpload={onUpload} busy={busy} />}
      {panel === 'os' && <NewOS o={o} types={types} onAction={onAction} busy={busy} />}
      {panel === 'note' && (
        <div className="flex gap-2">
          <label htmlFor={`qn-${r.key}`} className="sr-only">Observação</label>
          <input id={`qn-${r.key}`} value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-w-0 rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="Só a equipe vê. Ex.: cliente pediu cópia para o sócio" />
          <button type="button" disabled={busy || !note.trim()} onClick={() => { onAction(o, 'note', { note }); setNote(''); setPanel(''); }} className="bg-text text-white font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-40">Salvar</button>
        </div>
      )}
    </div>
  );
};

const ACTION_LABEL = { cobrar: 'Lembrar agora', os: 'Fazer agora', abrir_os: 'Abrir OS', certificado: 'Anexar certificado' };
// passos da OS que se resolvem com um clique (os outros pedem um dado: GRU, protocolo, nota fiscal)
const STEP_DONE_LABEL = { conferir: 'Conferido', cadastro: 'Cadastro feito', redigir: 'Redação pronta' };
const STEP_GO_LABEL = { gru: 'Registrar a GRU', pagamento: 'Ver a GRU', protocolo: 'Subir o protocolo', nf: 'Nota fiscal' };
// rola até o passo da OS, foca o primeiro campo e pisca o contorno
const focusStep = (osId, key) => setTimeout(() => {
  const el = document.getElementById(`os-${osId}-${key}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const inp = el.querySelector('input, textarea, button');
  if (inp) inp.focus({ preventScroll: true });
  el.classList.add('ring-2', 'ring-amber-400');
  setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2000);
}, 260);
const RowAction = ({ r, onAction, onExpand, busy }) => {
  if (r.action === 'os' && r.os && r.os.os) {
    const os = r.os.os; const st = (os.steps || []).find((x) => !x.done_at);
    const cls = 'inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3.5 text-sm font-semibold bg-text text-white hover:opacity-90 disabled:opacity-50 whitespace-nowrap';
    if (st && STEP_DONE_LABEL[st.key]) return <button type="button" disabled={busy} onClick={() => { if (window.confirm(`Marcar como feito: ${st.label}?`)) onAction({ session_id: r.o.session_id }, 'os_step', { os: os.id, step: st.key, info: '' }); }} className={cls}><Icon name="check" size={15} />{STEP_DONE_LABEL[st.key]}</button>;
    if (st) return <button type="button" onClick={() => { onExpand(); focusStep(os.id, st.key); }} className={cls}><Icon name="pencil" size={15} />{STEP_GO_LABEL[st.key] || 'Fazer agora'}</button>;
  }
  if (r.action === 'cobrar') return <button type="button" disabled={busy} onClick={() => { if (window.confirm(`Mandar agora para ${r.o.customer_email} o lembrete com o que falta?`)) onAction(r.o, 'remind'); }} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3.5 text-sm font-semibold bg-text text-white hover:opacity-90 disabled:opacity-50 whitespace-nowrap"><Icon name="send" size={15} />Lembrar agora</button>;
  if (r.action === 'credito') return <button type="button" disabled={busy} onClick={() => { if (window.confirm(`O dinheiro da invoice ${(r.o.invoice && r.o.invoice.number) || r.o.ref} caiu na conta?`)) onAction(r.o, 'payment_confirmed'); }} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3.5 text-sm font-semibold bg-text text-white hover:opacity-90 disabled:opacity-50 whitespace-nowrap"><Icon name="check" size={15} />Crédito confirmado</button>;
  if (r.action === 'auto') return (
    <span className="inline-flex flex-col items-end text-right" title="O robô Ivo manda os lembretes sozinho: dia 2, 4 e 9 depois do pagamento e depois toda semana, até o cliente completar.">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary-dark px-2.5 py-1 text-xs font-bold whitespace-nowrap"><Icon name="refresh" size={13} />Sistema cobrando</span>
      <span className="text-[11px] text-text/55 mt-1 whitespace-nowrap">{r.auto}</span>
    </span>
  );
  if (ACTION_LABEL[r.action]) return <button type="button" onClick={onExpand} className="inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3.5 text-sm font-semibold bg-text text-white hover:opacity-90 whitespace-nowrap">{ACTION_LABEL[r.action]}</button>;
  return r.o.client_url ? <a href={r.o.client_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 min-h-[40px] rounded-xl px-3.5 text-sm font-semibold bg-background-auxiliary text-text hover:bg-gray-200 whitespace-nowrap"><Icon name="eye" size={15} />Ver como o cliente</a> : null;
};

const ROW_TONE = { cliente: 'bg-amber-50 text-amber-800', conferir: 'bg-sky-50 text-sky-800', gru: 'bg-violet-50 text-violet-800', protocolar: 'bg-sky-50 text-sky-800', nf: 'bg-amber-50 text-amber-800', publicacao: 'bg-gray-100 text-gray-700', oposicao: 'bg-primary/10 text-primary-dark', exame: 'bg-primary/10 text-primary-dark', exigencia: 'bg-red-50 text-red-700', concedido: 'bg-primary/15 text-primary-dark' };
const ProcessRow = ({ r, open, onToggle, onAction, busy, compact, children }) => {
  const icon = (FUNNEL.find((f) => f[0] === r.group) || [])[2] || 'tag';
  return (
    <>
      <tr className={`border-t border-gray-100 ${open ? 'bg-gray-50/60' : ''}`}>
        <td className="py-3 pl-4 md:pl-5 pr-2">
          <div className="flex items-center gap-2.5">
            <button type="button" onClick={onToggle} aria-expanded={open} aria-label={`${open ? 'Fechar' : 'Abrir'} detalhes de ${r.mark}`} className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${open ? 'bg-text text-white' : 'bg-background-auxiliary text-text hover:bg-gray-200'}`}><Icon name={open ? 'up' : 'down'} size={16} strokeWidth={2.5} /></button>
            <MarkAvatar o={r.o} />
            <div className="min-w-0">
              <button type="button" onClick={onToggle} className="font-bold text-text hover:underline text-left">{r.mark}</button>
              <div className={`text-xs text-text/55 truncate ${compact ? 'max-w-[150px]' : 'max-w-[220px]'}`}>{r.customer}</div>
            </div>
            {r.test && <span className="text-[10px] font-bold bg-amber-50 text-amber-800 rounded-full px-2 py-0.5">TESTE</span>}
          </div>
        </td>
        <td className="py-3 px-2 text-sm text-text whitespace-nowrap">{compact ? (() => { const [a, ...b] = String(r.where || '').split(' · '); return <><div>{a}</div>{b.length > 0 && <div className="text-xs text-text/55">{b.join(' · ')}</div>}</>; })() : r.where}</td>
        <td className="py-3 px-2"><span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 whitespace-nowrap ${ROW_TONE[r.group] || ''}`}><Icon name={icon} size={14} />{r.stage}</span></td>
        <td className={`py-3 px-2 text-sm whitespace-nowrap ${r.urgent ? 'text-amber-800 font-bold' : 'text-text/75'}`}>{r.when}</td>
        <td className="py-3 px-2 text-sm text-text/75 whitespace-nowrap">{r.who}</td>
        <td className="py-3 pr-4 md:pr-5 pl-2 text-right"><RowAction r={r} onAction={onAction} onExpand={() => { if (!open) onToggle(); }} busy={busy} /></td>
      </tr>
      {open && <tr className="bg-gray-50/60"><td colSpan={6} className="px-4 md:px-5 pb-5">{children}</td></tr>}
    </>
  );
};

// a ficha completa de um pedido (o que antes ficava dentro do cartão)
const ProcessPage = ({ o, onBack, onAction, onUpload, busy, types, osOpen }) => {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const oss = osOpen.filter((x) => x.session_id === o.session_id);
  return (
    <div className="space-y-5">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-dark hover:underline min-h-[44px]"><Icon name="left" size={16} />Voltar para a lista</button>
      <section className="tmr-keep bg-text text-white rounded-3xl p-5 md:p-6 flex flex-wrap items-center gap-4">
        <MarkAvatar o={o} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary-light">{o.ref} · {o.kind_label}{o.classes ? ` · classes ${o.classes}` : ''}</div>
          <h1 className="text-2xl md:text-3xl font-bold break-words">{o.marks.join(', ') || o.trademark}</h1>
          <div className="text-sm text-gray-300">{o.customer_name} · {o.customer_email}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {o.client_url && <a href={o.client_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 min-h-[44px] rounded-xl px-4 bg-primary text-text font-bold text-sm"><Icon name="eye" size={16} />Ver como o cliente</a>}
          <button type="button" onClick={() => setEditing(!editing)} className="inline-flex items-center gap-2 min-h-[44px] rounded-xl px-4 bg-white/10 text-white font-semibold text-sm"><Icon name="pencil" size={16} />Editar cadastro</button>
        </div>
      </section>
      <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-5"><Steps steps={stepsOf(o, osOpen)} /></section>
      {oss.map((x) => <OSCard key={x.os.id} item={x} onAction={onAction} onUpload={onUpload} busy={busy} />)}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-5">
          <div className="bg-background-auxiliary rounded-xl p-4 mb-4 text-sm text-text"><span className="font-semibold">Próximo passo: </span>{NEXT[o.stage] || ''}</div>
          {editing ? <EditCadastro o={o} onAction={onAction} busy={busy} onClose={() => setEditing(false)} /> : (o.kind !== 'watch' && <DetailsBlock o={o} />)}
          <WatchBlock w={o.watch} />
          {o.filings.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-2">{o.kind === 'watch' ? 'Processos vigiados no INPI' : 'Processos no INPI'}</div>
              {o.filings.map((p) => (
                <div key={p.process} className="rounded-xl border border-gray-100 bg-white px-3 py-2 mb-1.5">
                  <div className="text-sm text-text"><span className="font-mono font-semibold">{p.process}</span> · {p.mark}{p.cls ? ` · classe ${p.cls}` : ''}</div>
                  <div className="text-xs text-text/60 mt-0.5">{[p.filed_at && `protocolado ${p.filed_at}`, p.protocol && `protocolo ${p.protocol}`, p.gru && `GRU ${p.gru}`, p.rpi && `publicado na RPI ${p.rpi}`, p.opp_until && `oposição até ${p.opp_until}`].filter(Boolean).join(' · ')}</div>
                </div>
              ))}
            </div>
          )}
          {o.team_notes && <pre className="text-xs text-text/70 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 font-sans">{o.team_notes}</pre>}
        </section>
        <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-5 space-y-4">
          <DocsBlock o={o} onUpload={onUpload} busy={busy} />
          <NewOS o={o} types={types} onAction={onAction} busy={busy} />
          <div>
            <label className="block text-sm font-semibold text-text mb-1" htmlFor={`n-${o.session_id}`}>Observação interna</label>
            <div className="flex gap-2">
              <input id={`n-${o.session_id}`} value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-w-0 rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="Só a equipe vê" />
              <button type="button" disabled={busy || !note.trim()} onClick={() => { onAction(o, 'note', { note }); setNote(''); }} className="bg-background-auxiliary text-text font-semibold rounded-xl px-4 py-2 text-sm disabled:opacity-40">Salvar</button>
            </div>
          </div>
          {o.client_url && <button type="button" disabled={busy} onClick={() => onAction(o, 'resend_access')} className="text-sm text-primary-dark font-semibold hover:underline disabled:opacity-50">Reenviar ao cliente o e-mail de acesso</button>}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-text/50 mb-3">Linha do tempo da marca</div>
            <ol className="relative border-l-2 border-gray-100 ml-3 space-y-3">
              {[...o.timeline].reverse().map((e, i) => (
                <li key={i} className="ml-5">
                  <span className="absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full bg-white border border-gray-200 text-primary-dark" aria-hidden="true"><Icon name={TL_ICON[e.stage] || 'clock'} size={13} /></span>
                  <div className="text-[11px] text-text/45">{fmtFull(e.at)}{e.by ? ` · ${e.by}` : ''}{e.client === false ? ' · interno' : ''}</div>
                  <div className="text-sm text-text leading-snug">{e.text || e.text_en}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </div>
    </div>
  );
};

const MAPA = [
  ['Pagou, faltam dados ou procuração', 'O sistema lembra o cliente sozinho (dia 2, 4, 9 e depois toda semana)', 'Robô Ivo · Belle no 4º lembrete'], ['Procuração chegou', 'Conferir e liberar', 'Belle / Carol'], ['Cadastro no e-INPI', 'Feito', 'Carol'], ['Emitir a GRU', 'Registrar a GRU', 'Carol'],
  ['Pagar a GRU', 'Comprovante', 'Gabriele'], ['Protocolar no INPI', 'Registrar protocolo', 'Belle / Carol'], ['Nota fiscal', 'Nota fiscal emitida', 'Carol'], ['Publicação e oposição', 'A Rita acompanha', 'Robô Rita'],
  ['Exigência ou oposição recebida', 'Abrir OS', 'Equipe'], ['Concedido', 'Anexar certificado', 'Carol'],
];

// barra "onde estão os processos": no topo (grade) ou na lateral (lista); cada pessoa escolhe e o navegador lembra
const LAYOUT_STORE = 'tmr_ops_layout';
const Funnel = ({ counts, filter, setFilter, side, onToggle }) => {
  const tone = (k, n, on) => {
    const warm = n > 0 && ['cliente', 'nf', 'exigencia'].includes(k);
    return on ? 'bg-text text-white border-text' : warm ? 'bg-amber-50 border-amber-200 text-amber-900' : n ? 'bg-primary/10 border-primary/25 text-primary-dark' : 'bg-gray-50 border-transparent text-text/55';
  };
  const toggle = (
    <button type="button" onClick={onToggle} className="hidden lg:inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-text hover:bg-gray-50" title={side ? 'Levar a barra para o topo' : 'Levar a barra para a lateral'}>
      <Icon name={side ? 'up' : 'left'} size={14} />{side ? 'Barra no topo' : 'Barra na lateral'}
    </button>
  );
  if (side) {
    return (
      <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-3" aria-labelledby="funil-side">
        <div className="flex items-center justify-between gap-2 px-1 mb-2">
          <h2 id="funil-side" className="text-xs font-bold uppercase tracking-wider text-text/55">Etapas</h2>
          {toggle}
        </div>
        <div className="flex flex-col gap-1.5">
          {FUNNEL.map(([k, label, icon]) => {
            const n = counts[k]; const on = filter === k;
            return (
              <button key={k} type="button" onClick={() => setFilter(on ? '' : k)} aria-pressed={on} className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 min-h-[44px] text-left ${tone(k, n, on)}`}>
                <Icon name={icon} size={17} />
                <span className="flex-1 text-[13px] font-semibold leading-tight">{label}</span>
                <span className="text-lg font-extrabold leading-none">{n}</span>
              </button>
            );
          })}
          {filter && <button type="button" onClick={() => setFilter('')} className="text-xs font-semibold text-primary-dark hover:underline mt-1">Mostrar todos</button>}
        </div>
      </section>
    );
  }
  return (
    <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-4" aria-labelledby="funil">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 id="funil" className="text-xs font-bold uppercase tracking-wider text-text/55">Onde estão os processos <span className="normal-case font-normal">· toque numa etapa para filtrar</span></h2>
        {toggle}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
        {FUNNEL.map(([k, label, icon]) => {
          const n = counts[k]; const on = filter === k;
          return (
            <button key={k} type="button" onClick={() => setFilter(on ? '' : k)} aria-pressed={on} className={`text-left rounded-xl px-3 py-2.5 flex flex-col gap-1 border min-h-[44px] ${tone(k, n, on)}`}>
              <Icon name={icon} size={18} />
              <span className="text-2xl font-extrabold leading-none">{n}</span>
              <span className="text-[11px] font-semibold leading-tight">{label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const greeting = () => { const h = Number(new Date().toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/Sao_Paulo' })); return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite'; };

const OpsPage = () => {
  const [key] = useState(readKey);
  const [name, setName] = useState(readName);
  const [data, setData] = useState(null);
  const [theme, toggleTheme] = useTheme('light', '#fcfcf9');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showTests, setShowTests] = useState(true);
  const [openRow, setOpenRow] = useState('');
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [full, setFull] = useState('');
  const [side, setSide] = useState(() => { try { return localStorage.getItem(LAYOUT_STORE) === 'side'; } catch (e) { return false; } });
  const toggleSide = () => { const v = !side; setSide(v); try { localStorage.setItem(LAYOUT_STORE, v ? 'side' : 'top'); } catch (e) { /* storage blocked */ } };

  useEffect(() => {
    const prev = document.title;
    document.title = 'Painel da equipe | TM RUSH';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = prev;
      document.head.removeChild(meta);
    };
  }, []);

  const load = useCallback(async () => {
    if (!key) return;
    try {
      const r = await fetch(`${API}/tmr-ops?k=${encodeURIComponent(key)}`);
      const j = await r.json();
      if (!j || !j.ok) throw new Error((j && j.error) || 'Sem acesso');
      setData(j);
      setError('');
    } catch (e) {
      setError(e.message || 'Não consegui carregar');
    }
  }, [key]);

  // atualiza a cada minuto só com a aba na tela (aba escondida não consulta o servidor)
  usePolling(load, 60000);

  const onAction = async (o, action, extra = {}) => {
    setBusy(true);
    setMsg('');
    try {
      const body = new URLSearchParams({ k: key, s: o.session_id, action, by: name || 'Equipe', ...extra });
      const r = await fetch(`${API}/tmr-ops-action`, { method: 'POST', body });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Não deu certo');
      setMsg(j.emailed ? 'Feito. O cliente recebeu o e-mail.' : j.notified ? 'Feito. O aviso foi por e-mail.' : 'Feito.');
      setTimeout(load, 1500);
    } catch (e) {
      setMsg(`Erro: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (o, file, { kind, label, client }) => {
    if (file.size > 3 * 1024 * 1024) { setMsg('Erro: arquivo grande demais (máximo 3 MB).'); return false; }
    setBusy(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('k', key); fd.append('s', o.session_id); fd.append('kind', kind); fd.append('label', label || ''); fd.append('client', client ? 'true' : 'false'); fd.append('by', name || 'Equipe'); fd.append('file', file);
      const r = await fetch(`${API}/tmr-ops-upload`, { method: 'POST', body: fd });
      const j = await r.json().catch(() => ({}));
      if (!j.ok) throw new Error(j.error || 'Não deu certo');
      setMsg(j.parsed && j.parsed.ok ? `Protocolo lido: processo ${j.parsed.process}.` : 'Documento anexado.');
      setTimeout(load, 1500);
      return j;
    } catch (e) {
      setMsg(`Erro: ${e.message}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const orders = useMemo(() => ((data && data.orders) || []).filter((o) => showTests || !o.is_test), [data, showTests]);
  const osOpen = useMemo(() => ((data && data.os_open) || []).filter((x) => showTests || !x.is_test), [data, showTests]);
  const msgOpen = ((data && data.msg_open) || []).filter((x) => showTests || !x.is_test);
  const types = (data && data.os_types) || [];
  const rows = useMemo(() => rowsOf(orders, osOpen), [orders, osOpen]);
  const counts = Object.fromEntries(FUNNEL.map(([k]) => [k, rows.filter((r) => r.group === k).length]));
  const todo = rows.filter((r) => r.todo);
  const ql = q.trim().toLowerCase();
  const shown = rows.filter((r) => (!filter || r.group === filter) && (!ql || [r.mark, r.customer, r.process, r.o.ref, r.o.customer_email].some((x) => String(x || '').toLowerCase().includes(ql))));
  const fullOrder = full ? orders.find((o) => o.session_id === full) : null;
  const goRow = (k) => { setFilter(''); setQ(''); setOpenRow(k); setTimeout(() => { const el = document.getElementById(`row-${k}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 80); };
  const inProgress = new Set(rows.filter((r) => r.group !== 'concedido').map((r) => r.o.session_id)).size;

  if (!key) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="bg-background-white rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
          <img src="/images/tm-rushlogo.svg" alt="TM RUSH" className="h-7 mx-auto mb-5" />
          <h1 className="text-xl font-bold text-text mb-2">Painel da equipe</h1>
          <p className="text-text/70 text-sm">Abra pelo link de acesso que você recebeu.</p>
          <a href="/entrar?ir=/ops" className="inline-block mt-5 text-sm font-semibold text-primary-dark hover:underline">Entrar com e-mail e senha</a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <header className="tmr-keep bg-text text-white">
        <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/images/tm-rushlogo.svg" alt="TM RUSH" className="tmr-logo h-6 brightness-0 invert" />
            <span className="text-sm font-semibold bg-white/10 border border-white/20 rounded-full px-3 py-1">Painel da equipe</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-300">
            <label className="flex items-center gap-2">
              Seu nome
              <input value={name} onChange={(e) => { setName(e.target.value); try { localStorage.setItem(NAME_STORE, e.target.value); } catch (err) { /* ignore */ } }} className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-white w-28" placeholder="Carol" />
            </label>
            <span>{data ? `Atualizado ${fmt(data.generated_at)}` : 'Carregando…'}</span>
            <ThemeButton theme={theme} onToggle={toggleTheme} className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 font-semibold text-white" />
            <button type="button" onClick={load} className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 font-semibold text-white">Atualizar</button>
          </div>
        </div>
      </header>
      <div className={`container mx-auto px-4 py-6 md:py-8 ${side && !fullOrder ? 'max-w-[1600px]' : 'max-w-7xl'}`}>
        {error && <div className="bg-red-50 text-red-700 rounded-xl p-4 mb-5 text-sm">{error}</div>}
        {msg && <div className="bg-primary/10 text-primary-dark rounded-xl p-4 mb-5 text-sm">{msg}</div>}

        {fullOrder ? (
          <ProcessPage o={fullOrder} onBack={() => setFull('')} onAction={onAction} onUpload={onUpload} busy={busy} types={types} osOpen={osOpen} />
        ) : (
          <div className={side ? 'lg:flex lg:items-start lg:gap-5' : ''}>
          {side && <aside className="hidden lg:block lg:w-56 lg:shrink-0 lg:sticky lg:top-4" aria-label="Etapas"><Funnel counts={counts} filter={filter} setFilter={setFilter} side onToggle={toggleSide} /></aside>}
          <div className="flex-1 min-w-0 space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-text">{greeting()}, Belle e Carol</h1>
                <p className="text-text/70 mt-1">{plural(inProgress, 'processo em andamento', 'processos em andamento')} · <b>{todo.length + msgOpen.length === 0 ? 'nada pendente com a equipe' : `${plural(todo.length + msgOpen.length, 'coisa', 'coisas')} para a equipe fazer`}</b></p>
              </div>
              <div className="w-full sm:w-80">
                <label htmlFor="busca" className="block text-xs font-semibold text-text/60 mb-1">Buscar marca, cliente ou nº do processo</label>
                <input id="busca" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex.: Waggy's ou 944832326" className="w-full min-h-[44px] rounded-xl border border-gray-200 bg-white px-3 text-sm" />
              </div>
            </div>

            {side
              ? <div className="lg:hidden"><Funnel counts={counts} filter={filter} setFilter={setFilter} side={false} onToggle={toggleSide} /></div>
              : <Funnel counts={counts} filter={filter} setFilter={setFilter} side={false} onToggle={toggleSide} />}

            <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-4 md:p-5" aria-labelledby="fazer">
              <h2 id="fazer" className="text-lg font-bold text-text mb-3 flex items-center gap-2"><Icon name="bell" size={20} className="text-amber-600" />Para a equipe fazer agora</h2>
              {!todo.length && !msgOpen.length && <div className="text-sm text-text/55">Nada pendente com a equipe. O sistema lembra os clientes sozinho; aparece aqui quando uma procuração chega, um cliente não responde aos lembretes ou o INPI publica algo que precise da equipe.</div>}
              <div className="space-y-2.5">
                {todo.map((r) => (
                  <div key={r.key} className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200/70 bg-amber-50/40 px-3 py-2.5">
                    <MarkAvatar o={r.o} />
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-bold text-text">{r.mark} · {r.stage}</div>
                      <div className="text-xs text-text/60">{r.where} · {r.when} · {r.who}</div>
                      {r.note && <div className="text-xs font-semibold text-amber-800 mt-0.5">{r.note}</div>}
                    </div>
                    <RowAction r={r} onAction={onAction} onExpand={() => goRow(r.key)} busy={busy} />
                    <button type="button" onClick={() => goRow(r.key)} className="inline-flex items-center gap-1 min-h-[40px] rounded-xl px-3 text-sm font-semibold bg-white border border-gray-200 text-text hover:bg-gray-50">Abrir<Icon name="down" size={14} /></button>
                  </div>
                ))}
                {msgOpen.map((x) => <MsgCard key={x.msg.id} item={x} onAction={onAction} busy={busy} />)}
              </div>
            </section>

            <section className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm pt-4 md:pt-5 pb-2" aria-labelledby="todos">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 mb-2">
                <h2 id="todos" className="text-lg font-bold text-text">Todos os processos{filter ? ` · ${(FUNNEL.find((f) => f[0] === filter) || [])[1]}` : ''}</h2>
                <div className="flex flex-wrap items-center gap-3">
                  {filter && <button type="button" onClick={() => setFilter('')} className="text-sm font-semibold text-primary-dark hover:underline">Mostrar todos</button>}
                  <label className="flex items-center gap-2 text-sm text-text/70"><input type="checkbox" checked={showTests} onChange={(e) => setShowTests(e.target.checked)} /> Pedidos de teste</label>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] border-collapse">
                  <thead>
                    <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-text/50">
                      <th scope="col" className="py-2 pl-4 md:pl-5 pr-2">Marca</th><th scope="col" className="py-2 px-2">Classe · processo</th><th scope="col" className="py-2 px-2">Etapa</th><th scope="col" className="py-2 px-2">Prazo ou desde</th><th scope="col" className="py-2 px-2">Quem</th><th scope="col" className="py-2 pr-4 md:pr-5 pl-2 text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FUNNEL.filter(([k]) => shown.some((r) => r.group === k)).map(([k, label]) => (
                      <React.Fragment key={k}>
                        <tr><td colSpan={6} className={`px-4 md:px-5 pt-3 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider border-t border-gray-100 ${['cliente', 'nf', 'exigencia'].includes(k) ? 'text-amber-800 bg-amber-50/50' : 'text-primary-dark bg-primary/5'}`}>{label} ({shown.filter((r) => r.group === k).length}){['publicacao', 'oposicao', 'exame'].includes(k) ? ' · a Rita acompanha' : ''}</td></tr>
                        {shown.filter((r) => r.group === k).map((r) => (
                          <ProcessRow key={r.key} r={r} open={openRow === r.key} onToggle={() => setOpenRow(openRow === r.key ? '' : r.key)} onAction={onAction} busy={busy} compact={side}>
                            <RowDetail r={r} osOpen={osOpen} onAction={onAction} onUpload={onUpload} busy={busy} types={types} onOpenFull={(s) => { setFull(s); window.scrollTo(0, 0); }} />
                          </ProcessRow>
                        ))}
                      </React.Fragment>
                    ))}
                    {data && !shown.length && <tr><td colSpan={6} className="text-center text-text/50 py-12 text-sm">{rows.length ? 'Nada com esse filtro.' : 'Nenhum pedido ainda.'}</td></tr>}
                  </tbody>
                </table>
              </div>
            </section>

            <details className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm p-4 md:p-5">
              <summary className="cursor-pointer text-lg font-bold text-text min-h-[44px] flex items-center">Mapa das etapas: o que cada linha pede</summary>
              <p className="text-sm text-text/60 mt-2 mb-3">Cada processo mostra só o botão da etapa em que está. Feito o passo, a linha anda sozinha para a próxima.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {MAPA.map(([what, btn, who], i) => (
                  <div key={what} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5 text-sm">
                    <b className="w-6 text-text/45">{i + 1}</b><span className="flex-1 text-text">{what}</span><span className="text-xs text-text/55">{who}</span>
                    <span className={`text-xs font-bold rounded-lg px-2 py-1 ${who === ROBOT ? 'bg-primary/10 text-primary-dark' : 'bg-text text-white'}`}>{btn}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default OpsPage;
